import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, X, Loader2 } from "lucide-react";
import { useDebounce } from "@/hooks/useDebounce";

interface Tenant {
  tenant_id: string;
  name: string;
  slug: string | null;
  domain: string | null;
  status: string | null;
  plan_id: string | null;
  max_users: number | null;
  max_phone_numbers: number | null;
  max_sms_per_day: number | null;
  billing_email: string | null;
  billing_via_agency: boolean | null;
}

interface CustomerFormDialogProps {
  tenant: Tenant | null;
  onClose: () => void;
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function CustomerFormDialog({
  tenant,
  onClose,
}: CustomerFormDialogProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: tenant?.name || "",
    slug: tenant?.slug || "",
    domain: tenant?.domain || "",
    status: tenant?.status || "active",
    plan_id: tenant?.plan_id || "",
    max_users: tenant?.max_users?.toString() || "",
    max_phone_numbers: tenant?.max_phone_numbers?.toString() || "",
    max_sms_per_day: tenant?.max_sms_per_day?.toString() || "",
    billing_email: tenant?.billing_email || "",
    billing_via_agency: tenant?.billing_via_agency ?? true,
  });

  const [slugStatus, setSlugStatus] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const debouncedSlug = useDebounce(formData.slug, 500);

  // Auto-generate slug from name for new tenants
  useEffect(() => {
    if (!tenant && formData.name && !formData.slug) {
      setFormData((prev) => ({ ...prev, slug: generateSlug(formData.name) }));
    }
  }, [formData.name, tenant]);

  // Check slug availability
  useEffect(() => {
    if (!debouncedSlug || debouncedSlug === tenant?.slug) {
      setSlugStatus("idle");
      return;
    }

    const checkSlug = async () => {
      setSlugStatus("checking");
      try {
        const { data, error } = await supabase.functions.invoke("Calie_checkSlug", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          body: { slug: debouncedSlug },
        });
        
        if (error) throw error;
        setSlugStatus(data?.available ? "available" : "taken");
      } catch {
        setSlugStatus("idle");
      }
    };

    checkSlug();
  }, [debouncedSlug, tenant?.slug]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { data: result, error } = await supabase.functions.invoke("Calie_createTenant", {
        body: {
          name: data.name,
          slug: data.slug,
          domain: data.domain || null,
          status: data.status,
          plan_id: data.plan_id || null,
          max_users: data.max_users ? parseInt(data.max_users) : null,
          max_phone_numbers: data.max_phone_numbers ? parseInt(data.max_phone_numbers) : null,
          max_sms_per_day: data.max_sms_per_day ? parseInt(data.max_sms_per_day) : null,
          billing_email: data.billing_email || null,
          billing_via_agency: data.billing_via_agency,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to create customer");
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-tenants"] });
      toast.success("Customer created successfully");
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { data: result, error } = await supabase.functions.invoke("Calie_updateTenant", {
        body: {
          tenant_id: tenant?.tenant_id,
          name: data.name,
          slug: data.slug,
          domain: data.domain || null,
          status: data.status,
          plan_id: data.plan_id || null,
          max_users: data.max_users ? parseInt(data.max_users) : null,
          max_phone_numbers: data.max_phone_numbers ? parseInt(data.max_phone_numbers) : null,
          max_sms_per_day: data.max_sms_per_day ? parseInt(data.max_sms_per_day) : null,
          billing_email: data.billing_email || null,
          billing_via_agency: data.billing_via_agency,
        },
      });

      if (error) {
        throw new Error(error.message || "Failed to update customer");
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agency-tenants"] });
      toast.success("Customer updated successfully");
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (slugStatus === "taken") {
      toast.error("Slug is already taken");
      return;
    }
    if (tenant) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Acme Corp"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slug">Slug *</Label>
          <div className="relative">
            <Input
              id="slug"
              value={formData.slug}
              onChange={(e) =>
                setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })
              }
              placeholder="acme-corp"
              required
              className="pr-8"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {slugStatus === "checking" && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
              {slugStatus === "available" && (
                <Check className="h-4 w-4 text-green-500" />
              )}
              {slugStatus === "taken" && (
                <X className="h-4 w-4 text-red-500" />
              )}
            </div>
          </div>
          {slugStatus === "taken" && (
            <p className="text-sm text-destructive">This slug is already taken</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="domain">Domain</Label>
          <Input
            id="domain"
            value={formData.domain}
            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
            placeholder="acme.com"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="status">Status</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({ ...formData, status: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="plan_id">Plan</Label>
          <Input
            id="plan_id"
            value={formData.plan_id}
            onChange={(e) => setFormData({ ...formData, plan_id: e.target.value })}
            placeholder="pro"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="billing_email">Billing Email</Label>
          <Input
            id="billing_email"
            type="email"
            value={formData.billing_email}
            onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
            placeholder="billing@acme.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="max_users">Max Users</Label>
          <Input
            id="max_users"
            type="number"
            value={formData.max_users}
            onChange={(e) => setFormData({ ...formData, max_users: e.target.value })}
            placeholder="10"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="max_phone_numbers">Max Phone Numbers</Label>
          <Input
            id="max_phone_numbers"
            type="number"
            value={formData.max_phone_numbers}
            onChange={(e) => setFormData({ ...formData, max_phone_numbers: e.target.value })}
            placeholder="5"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="max_sms_per_day">Max SMS/Day</Label>
          <Input
            id="max_sms_per_day"
            type="number"
            value={formData.max_sms_per_day}
            onChange={(e) => setFormData({ ...formData, max_sms_per_day: e.target.value })}
            placeholder="100"
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Switch
            id="billing_via_agency"
            checked={formData.billing_via_agency}
            onCheckedChange={(checked) =>
              setFormData({ ...formData, billing_via_agency: checked })
            }
          />
          <Label htmlFor="billing_via_agency">Bill through agency</Label>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting || slugStatus === "taken"}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {tenant ? "Updating..." : "Creating..."}
            </>
          ) : tenant ? (
            "Update Customer"
          ) : (
            "Create Customer"
          )}
        </Button>
      </div>
    </form>
  );
}
